// Pure cadence maths for global recurring tasks. Callers supply Melbourne
// YYYY-MM-DD dates (usually from melbourneToday()); all arithmetic stays on
// UTC-anchored calendar components so host timezone and DST never move a day.

export const RECURRENCE_CADENCES = ['weekly', 'monthly'] as const;
export type RecurrenceCadence = (typeof RECURRENCE_CADENCES)[number];

export const WEEKLY_ANCHORS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

const WEEKDAY_INDEX: Record<(typeof WEEKLY_ANCHORS)[number], number> = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
};

function parts(iso: string): { year: number; month: number; day: number } {
  const [year, month, day] = iso.split('-').map(Number);
  return { year: year!, month: month!, day: day! };
}

function formatUtc(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

function formatDate(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function lastDayOfMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function isValidRecurrenceAnchor(cadence: RecurrenceCadence, anchor: string): boolean {
  if (cadence === 'weekly') return WEEKLY_ANCHORS.includes(anchor as (typeof WEEKLY_ANCHORS)[number]);
  if (anchor === 'last') return true;
  return /^(?:[1-9]|1\d|2[0-8])$/.test(anchor);
}

/** First cadence occurrence strictly after `after`. */
export function nextOccurrence(cadence: RecurrenceCadence, anchor: string, after: string): string {
  const { year, month, day } = parts(after);

  if (cadence === 'weekly') {
    const target = WEEKDAY_INDEX[anchor as (typeof WEEKLY_ANCHORS)[number]];
    if (target === undefined) throw new Error(`Invalid weekly recurrence anchor: ${anchor}`);
    const date = new Date(Date.UTC(year, month - 1, day));
    const delta = (target - date.getUTCDay() + 7) % 7 || 7;
    date.setUTCDate(date.getUTCDate() + delta);
    return formatUtc(date);
  }

  if (!isValidRecurrenceAnchor(cadence, anchor)) {
    throw new Error(`Invalid monthly recurrence anchor: ${anchor}`);
  }

  if (anchor === 'last') {
    const thisMonthLast = lastDayOfMonth(year, month);
    if (day < thisMonthLast) return formatDate(year, month, thisMonthLast);
    const nextMonth = new Date(Date.UTC(year, month, 1));
    const nextYear = nextMonth.getUTCFullYear();
    const nextMonthNumber = nextMonth.getUTCMonth() + 1;
    return formatDate(nextYear, nextMonthNumber, lastDayOfMonth(nextYear, nextMonthNumber));
  }

  const targetDay = Number(anchor);
  if (day < targetDay) return formatDate(year, month, targetDay);
  const nextMonth = new Date(Date.UTC(year, month, targetDay));
  return formatUtc(nextMonth);
}

/** First cadence occurrence on or after `today`. */
export function initialNextDue(cadence: RecurrenceCadence, anchor: string, today: string): string {
  const { year, month, day } = parts(today);
  if (!isValidRecurrenceAnchor(cadence, anchor)) {
    throw new Error(`Invalid ${cadence} recurrence anchor: ${anchor}`);
  }

  if (cadence === 'weekly') {
    const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
    if (weekday === WEEKDAY_INDEX[anchor as (typeof WEEKLY_ANCHORS)[number]]) return today;
  } else if (anchor === 'last') {
    if (day === lastDayOfMonth(year, month)) return today;
  } else if (day === Number(anchor)) {
    return today;
  }

  return nextOccurrence(cadence, anchor, today);
}
